import { filterFreeSlots, type SlotBooking } from '@atink/domain';
import { selfBookLinkByToken, selfBookLinkKey, useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { makePrng } from '@/utils/seededRandom';
import { resolveDayOff } from '@/domain/artistAdmin';
import { holdExpiresAtIso, isThisWeek } from '@/domain/depositWindow';
import { holdSelfBookSlotIntent } from '@/api/intents';
import { useUIStore } from '@/state/uiStore';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { persistRecord } from '@/api/persist';
import {
  AWAITING_CLIENT,
  generateDepositLink,
  generateSelfBookLink,
  simulateStripeWebhookPaid,
} from '@/domain/depositFlow';
import { SESSION_DEPOSIT_AUD, SESSION_DURATION_MIN } from '@/domain/pricing';
import { normalizeInstagramHandle } from '@/domain/instagram';
import type { Artist, Booking, Channel, CustomerRecord, Deposit, SelfBookLink, StudioId } from '@/domain/types';

// The reserved held-slot identity lives in depositFlow (avoids an import cycle);
// re-exported here so existing imports from selfBookFlow keep resolving.
export { AWAITING_CLIENT } from '@/domain/depositFlow';

// The universal link supports small / half / full. On the OPEN client-pick flow
// the client still only picks half/full (09-Jul lock); 'small' is reachable only
// when the ARTIST pins it, so the client never chooses a length from a menu.
export type SelfBookSession = 'small' | 'half-day' | 'full-day';

// The two possible start times a client can pick (client-locked, 09 Jul).
export const SELF_BOOK_STARTS = ['11:00', '15:00'] as const;
export type SelfBookStart = (typeof SELF_BOOK_STARTS)[number];

// Slot durations used for the night-rule fit check + the created booking.
const SESSION_DURATION_HOURS: Record<SelfBookSession, number> = {
  small: 1.5,
  'half-day': 4,
  'full-day': 8,
};

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

function hourOf(hhmm: string): number {
  return parseInt(hhmm.slice(0, 2), 10);
}

/** Next unique per-booking reference for a link, e.g. AL-LOCKE-4F7K-03. */
function mintBookingReference(link: SelfBookLink): string {
  const prefix = `${link.depositReference}-`;
  const used = Object.values(useBrainStore.getState().deposits).filter(
    (d) => d.depositReference?.startsWith(prefix),
  ).length;
  return `${prefix}${String(used + 1).padStart(2, '0')}`;
}

/**
 * Deterministic reconciliation code for an artist's universal link, e.g.
 * "AL-ASHER-4F7K": initials + surname + a stable 4-char code seeded from the
 * artist id so re-runs mint the same reference (deterministic, no Date.now).
 */
export function mintDepositReference(artist: Artist): string {
  const initials = `${artist.firstName[0] ?? ''}${artist.lastName[0] ?? ''}`.toUpperCase();
  const surname = artist.lastName.replace(/[^a-zA-Z]/g, '').toUpperCase() || artist.id.toUpperCase();
  let seed = 0;
  for (const ch of artist.id) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
  const rng = makePrng(seed);
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 4; i += 1) code += alphabet[Math.floor(rng() * alphabet.length)];
  return `${initials}-${surname}-${code}`;
}

/**
 * The artist's one universal, reusable self-book link. Customer-agnostic: no
 * customerId, no pinned session/deposit. Returns the existing link if the artist
 * already has one, else mints it (with a stable deposit reference).
 */
export function getOrCreateUniversalLink(opts: { artistId: string; studioId: StudioId }): SelfBookLink {
  const brain = useBrainStore.getState();
  const existing = Object.values(brain.selfBookLinks).find(
    (l) => l.universal && l.artistId === opts.artistId,
  );
  if (existing) return existing;

  const artist = brain.artists[opts.artistId];
  const now = new Date(nowIso());
  const oneYearOut = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString();
  return generateSelfBookLink({
    artistId: opts.artistId,
    studioId: opts.studioId,
    dateRangeStart: nowIso(),
    dateRangeEnd: oneYearOut,
    expiresAt: oneYearOut,
    channelSent: 'sms',
    universal: true,
    depositReference: artist ? mintDepositReference(artist) : `REF-${opts.artistId.toUpperCase()}`,
  });
}

export interface PinSlotResult {
  link: SelfBookLink;
  deposit: Deposit;
  booking: Booking;
}

/** Release a link's current live hold (if any) so a re-pin doesn't orphan it.
 *  Non-destructive: the superseded deposit becomes a findable 'expired' record,
 *  its held ghost booking is cancelled so the slot reopens. Store-side only: the
 *  served build's hold intent does this on the server (the caller mirrors it). */
function releasePinnedHold(link: SelfBookLink): void {
  const brain = useBrainStore.getState();
  const priorId = link.pinnedDepositId;
  if (!priorId) return;
  const prior = brain.deposits[priorId];
  if (!prior || prior.status !== 'pending') return; // already paid/expired -> nothing to release
  brain.upsertDeposit({ ...prior, status: 'expired' });
  const bk = brain.bookings[prior.heldBookingId ?? `bk-${prior.id}`];
  if (bk && bk.status === 'held') {
    brain.upsertBooking({ ...bk, status: 'cancelled' });
  }
}

/**
 * Hold a slot for the artist's universal link: a pending deposit (unique
 * reconciliation ref) + a held ghost booking on that slot, pinned onto the link.
 * Both carry AWAITING_CLIENT until a client pays.
 *
 * B5 (7fr8): served, the hold is the SERVER's (POST hold-self-book-slot — the
 * awaiting-client records used to be local to the artist's tab, so no other
 * device saw the hold and it never lapsed). It is mirrored here straight away
 * (server-authored, no bridge PUTs) and reverted with a toast if refused. It
 * lapses on the link window (24h, +2h this week), like any deposit link.
 */
export function pinSelfBookSlot(opts: {
  link: SelfBookLink;
  session: SelfBookSession;
  startIso: string;
  /**
   * K4 custom hold: the artist set their own deposit + duration (half-hour steps).
   * `session` still tags the nearest bracket for downstream sessionLength consumers;
   * these values override the bracket's standard deposit amount and duration.
   */
  custom?: { depositAud: number; durationMin: number };
}): PinSlotResult {
  const brain = useBrainStore.getState();
  const { link, session, startIso, custom } = opts;
  const now = nowIso();
  const stamp = `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
  const depositId = `dep-pin-${stamp}`;
  const bookingId = `bk-pin-${stamp}`;
  const amountAud = custom ? custom.depositAud : SESSION_DEPOSIT_AUD[session];
  const durationMin = custom ? custom.durationMin : SESSION_DURATION_MIN[session];
  const depositReference = mintBookingReference(link);
  const overbookable = isThisWeek(startIso, now);

  const deposit: Deposit = {
    id: depositId,
    customerId: AWAITING_CLIENT,
    studioId: link.studioId,
    artistId: link.artistId,
    amountAud,
    feesAud: Number((amountAud * 0.0175 + 0.3).toFixed(2)),
    netAud: Number((amountAud - amountAud * 0.0175 - 0.3).toFixed(2)),
    status: 'pending',
    channelSent: 'sms',
    stripePaymentLinkId: `plink_mock_${depositId}`,
    expiresAt: holdExpiresAtIso(startIso, now),
    createdAt: now,
    policy: 'seven-day-rule',
    source: 'artist-link',
    sentByRole: 'self-book',
    depositReference,
    heldBookingId: bookingId,
  };
  const booking: Booking = {
    id: bookingId,
    customerId: AWAITING_CLIENT,
    studioId: link.studioId,
    artistId: link.artistId,
    start: startIso,
    durationMin,
    sessionLength: session,
    status: 'held',
    depositId,
    ...(overbookable ? { overbookable: true } : {}),
    designBrief: 'Held for a self-book link. Awaiting client.',
    referencePhotos: [],
    source: 'artist-link',
    createdAt: now,
  };
  const pinned: SelfBookLink = {
    ...link,
    pinnedStartIso: startIso,
    sessionLength: session,
    pinnedDepositId: depositId,
    // The hold amount lives on the link too (tv-2na.8.4): the public page and the
    // server charge exactly what the artist held.
    depositAud: amountAud,
  };

  if (isStoreFallbackEnabled()) {
    releasePinnedHold(link);
    brain.upsertDeposit(deposit);
    brain.upsertBooking(booking);
    brain.upsertSelfBookLink(pinned);
    return { link: pinned, deposit, booking };
  }

  const prior = link.pinnedDepositId ? brain.deposits[link.pinnedDepositId] : undefined;
  const priorBooking = prior?.heldBookingId ? brain.bookings[prior.heldBookingId] : undefined;
  const linkKey = selfBookLinkKey(brain.selfBookLinks, link.token);
  const touched = [
    ['deposits', depositId],
    ['bookings', bookingId],
    ['selfBookLinks', linkKey],
    ...(prior ? ([['deposits', prior.id]] as const) : []),
    ...(priorBooking ? ([['bookings', priorBooking.id]] as const) : []),
  ] as Array<readonly [string, string]>;
  withServerAuthoredWrites(touched as never, () => {
    releasePinnedHold(link);
    brain.upsertDeposit(deposit);
    brain.upsertBooking(booking);
    brain.upsertSelfBookLink(pinned);
  });
  void holdSelfBookSlotIntent({
    token: link.token,
    startIso,
    session,
    ...(custom ? { custom } : {}),
    depositReference,
    depositId,
    bookingId,
  }).then((res) => {
    if (res.ok) {
      // The server's answer is the hold: its expiry, amount, length and over-bookable flag.
      withServerAuthoredWrites(touched as never, () => {
        const st = useBrainStore.getState();
        const d = st.deposits[depositId];
        if (d) st.upsertDeposit({ ...d, amountAud: res.depositAud || d.amountAud, ...(res.expiresAt ? { expiresAt: res.expiresAt } : {}) });
        const b = st.bookings[bookingId];
        if (b) st.upsertBooking({ ...b, durationMin: res.durationMin || b.durationMin, overbookable: res.overbookable || undefined });
        const l = selfBookLinkByToken(st.selfBookLinks, link.token);
        if (l && res.depositAud) st.upsertSelfBookLink({ ...l, depositAud: res.depositAud });
      });
      return;
    }
    // Refused (the slot was just taken, say): put the store back as it was.
    withServerAuthoredWrites(touched as never, () => {
      useBrainStore.setState((s) => {
        const deposits = { ...s.deposits };
        delete deposits[depositId];
        if (prior) deposits[prior.id] = prior;
        const bookings = { ...s.bookings };
        delete bookings[bookingId];
        if (priorBooking) bookings[priorBooking.id] = priorBooking;
        return { deposits, bookings, selfBookLinks: { ...s.selfBookLinks, [linkKey]: link } };
      });
    });
    useUIStore.getState().pushNotification({
      intensity: 'critical',
      title: "Couldn't hold that slot",
      body: res.message ?? 'The slot was not held. Try another time.',
      ttlMs: 8000,
    });
  });
  return { link: pinned, deposit, booking };
}

/** ISO start for a given calendar day + one of the fixed start times. */
export function slotStartIso(day: Date, start: SelfBookStart): string {
  const d = new Date(day);
  d.setHours(hourOf(start), 0, 0, 0);
  return d.toISOString();
}

/** A firm busy range from the public page bundle (no ids / customers). */
export interface SelfBookBusyRange {
  start: string;
  durationMin: number;
}

/**
 * Which of the two fixed starts (11:00 / 15:00) the artist offers on `day` for
 * the chosen session. Pure (the public page feeds it the server bundle; the
 * artist week feeds it the store). Applies:
 *  - day-off (pattern / RDO / leave) → no starts,
 *  - the night rule: a start is offered only when the session fits before the
 *    day's pattern end, OR the artist has opted into nightSessions,
 *  - first-to-pay-wins, by the DB double-book rule (tv-2na.8.4): a start whose
 *    [start, start + session) range overlaps a FIRM booking of this artist is
 *    dropped (filterFreeSlots). Held / pending / rescheduled / overbookable
 *    bookings never block — the DB would accept the paid booking over them.
 */
export function selfBookStartsForDay(opts: {
  /** The artist's availability, or the public bundle's stripped copy of it. */
  availability: {
    pattern: Partial<Record<'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat', { startTime: string; endTime: string } | null>>;
    rdoDates: string[];
    leaveBlocks: ReadonlyArray<{ startDate: string; endDate: string }>;
    nightSessions?: boolean;
  };
  artistId: string;
  day: Date;
  session: SelfBookSession;
  /** Store bookings (any status; the firm rule filters them). */
  bookings?: readonly SlotBooking[] | Readonly<Record<string, SlotBooking>>;
  /** Firm busy ranges from the public bundle. */
  busy?: readonly SelfBookBusyRange[];
}): SelfBookStart[] {
  const { availability, artistId, day, session } = opts;
  // resolveDayOff only needs the leave dates to decide "off" (type/note just label it).
  if (resolveDayOff(availability as unknown as Artist['availability'], day)) return [];

  const dowKey = (['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const)[day.getDay()]!;
  const window = availability.pattern[dowKey];
  if (!window) return [];
  const patternEndHour = hourOf(window.endTime);
  const durationHours = SESSION_DURATION_HOURS[session];
  const nightSessions = availability.nightSessions === true;

  const offered = SELF_BOOK_STARTS.filter((start) => {
    const startHour = hourOf(start);
    if (startHour < hourOf(window.startTime)) return false;
    const fits = startHour + durationHours <= patternEndHour;
    return fits || nightSessions;
  });

  const blockers: SlotBooking[] = [
    ...(opts.bookings ? Object.values(opts.bookings) : []),
    ...(opts.busy ?? []).map((b, i) => ({
      id: `busy-${i}`,
      artistId,
      start: b.start,
      durationMin: b.durationMin,
      status: 'confirmed',
    })),
  ];
  const candidates = offered.map((start) => {
    const startIso = slotStartIso(day, start);
    const endIso = new Date(new Date(startIso).getTime() + durationHours * 3_600_000).toISOString();
    return { start, artistId, startIso, endIso };
  });
  return filterFreeSlots(candidates, blockers).map((c) => c.start);
}

/** Store-backed wrapper (artist week / fallback): the same rule over brain.bookings. */
export function startOptionsForDay(
  artist: Artist,
  day: Date,
  session: SelfBookSession,
): SelfBookStart[] {
  return selfBookStartsForDay({
    availability: artist.availability,
    artistId: artist.id,
    day,
    session,
    bookings: useBrainStore.getState().bookings as Readonly<Record<string, SlotBooking>>,
  });
}

const NON_DIGIT = /\D/g;

export type CustomerMatch =
  | { kind: 'matched'; id: string; via: 'phone' | 'email' | 'instagram' }
  | { kind: 'none'; nameHints: string[] };

/** Strong-ID match: an exact, normalized hit on phone, email OR instagram handle
 *  is the same customer (precedence phone > email > handle). Name never matches
 *  alone — a name hit is returned as `nameHints` for a soft UI hint only. */
export function matchCustomer(input: {
  mobile?: string; email?: string; instagramHandle?: string; name?: string;
}): CustomerMatch {
  const customers = Object.values(useBrainStore.getState().customers);
  const mobileDigits = (input.mobile ?? '').replace(NON_DIGIT, '');
  const emailNorm = (input.email ?? '').trim().toLowerCase();
  const handleNorm = normalizeInstagramHandle(input.instagramHandle ?? '');
  const nameNorm = (input.name ?? '').trim().toLowerCase();

  const byPhone = mobileDigits
    ? customers.find((c) => c.phone.replace(NON_DIGIT, '') === mobileDigits)
    : undefined;
  const byEmail = emailNorm
    ? customers.find((c) => c.email?.trim().toLowerCase() === emailNorm)
    : undefined;
  const byHandle = handleNorm
    ? customers.find((c) => normalizeInstagramHandle(c.instagramHandle ?? '') === handleNorm)
    : undefined;
  if (byPhone) return { kind: 'matched', id: byPhone.id, via: 'phone' };
  if (byEmail) return { kind: 'matched', id: byEmail.id, via: 'email' };
  if (byHandle) return { kind: 'matched', id: byHandle.id, via: 'instagram' };

  const nameHints = nameNorm
    ? customers.filter((c) => `${c.firstName} ${c.lastName}`.trim().toLowerCase() === nameNorm).map((c) => c.id)
    : [];
  return { kind: 'none', nameHints };
}

/** Create a customer with an opaque id (not derived from PII) + sensible defaults. */
export function createCustomer(input: {
  name: string; mobile?: string; email?: string; instagramHandle?: string;
  studioId: StudioId; sourceChannel?: Channel;
}): string {
  const [firstName, ...rest] = input.name.trim().split(/\s+/);
  const handle = normalizeInstagramHandle(input.instagramHandle ?? '');
  const emailNorm = (input.email ?? '').trim().toLowerCase();
  const id = `cust-${crypto.randomUUID().slice(0, 8)}`;
  const customer: CustomerRecord = {
    id,
    firstName: firstName ?? input.name.trim(),
    lastName: rest.join(' '),
    phone: (input.mobile ?? '').trim(),
    email: emailNorm || undefined,
    instagramHandle: handle || undefined,
    ageBand: '25-34',
    suburb: '', postcode: '', state: 'VIC',
    distanceToNearestStudioKm: 0,
    nearestStudioId: input.studioId,
    primaryStudioId: input.studioId,
    source: { channel: input.sourceChannel ?? 'web-form', firstTouchAt: nowIso() },
    leadScore: 50, leadStage: 'deposit-pending', toneTag: 'casual-young',
    engagementStatus: 'active',
    conversation: [], rapportNotes: [], futureReadinessSignals: [],
    bookings: [], pieces: [], deposits: [],
    creditOnFile: { balanceAud: 0, history: [] },
    crossStudioTouches: [], doNotContact: false, lifetimeSpendAud: 0,
  };
  useBrainStore.getState().upsertCustomer(customer);
  return id;
}

/** Back-compat wrapper for existing non-interactive callers (self-book, deposit,
 *  walk-in): reuse on any strong-ID match, else create. No prompt. */
export function matchOrCreateCustomer(input: {
  name: string; email: string; mobile: string; studioId: StudioId; instagramHandle?: string;
}): string {
  const m = matchCustomer(input);
  return m.kind === 'matched' ? m.id : createCustomer(input);
}

/** C6: notify the booking artist first, in a system→artist DM thread. */
export function notifyArtistOfSelfBook(opts: { artistId: string; customerId: string; body: string }): void {
  const brain = useBrainStore.getState();
  const threadId = brain.openOrCreateDm(['system-bot', opts.artistId], 'system-bot', nowIso());
  brain.sendDmMessage(threadId, opts.body, {
    authorId: 'system-bot',
    timestamp: nowIso(),
    customerRefId: opts.customerId,
  });
}

export interface CompleteSelfBookInput {
  link: SelfBookLink;
  session: SelfBookSession;
  startIso: string;
  details: { name: string; email: string; mobile: string };
  designBrief: string;
  referencePhotos: string[];
}

export interface CompleteSelfBookResult {
  customerId: string;
  depositId: string;
  bookingId: string;
  heldUntilIso: string;
}

/**
 * Payment completion for the universal link. Resolves the customer (match-or-
 * create). If the slot was already pinned by the artist (pinSelfBookSlot), a
 * held deposit + ghost booking already exist under AWAITING_CLIENT - attach the
 * resolved customer to that same deposit and confirm through the normal Stripe
 * webhook path, WITHOUT minting a second deposit. Otherwise (the open,
 * client-pick path) mint a fresh deposit stamped with the link's reference, then
 * run the NORMAL Stripe webhook path (simulateStripeWebhookPaid) so the booking,
 * confirmation cadence and waiver all fire exactly as a regular booking. Adds the
 * immediate confirmation text into the customer thread and notifies the artist
 * first (C6), then the shared studio channel.
 */
export function completeUniversalSelfBook(input: CompleteSelfBookInput): CompleteSelfBookResult {
  const brain = useBrainStore.getState();
  const { link } = input;

  // Identity resolved entirely from what the client entered (universal link has
  // no customerId); a staff-sent named link keeps its customer. Track whether we
  // MINTED a fresh customer: server-backed, a brand-new customer must be committed
  // BEFORE the atomic create-and-pay intent (bookings/deposits carry a NOT-NULL FK
  // to customers, and the single-POST intent — unlike the old write queue — cannot
  // retry an FK miss), so we persist it first in beforeAtomicIntent below.
  let customerId: string;
  let mintedNewCustomer = false;
  if (link.customerId) {
    customerId = link.customerId;
  } else {
    const match = matchCustomer(input.details);
    if (match.kind === 'matched') {
      customerId = match.id;
    } else {
      customerId = createCustomer({ ...input.details, studioId: link.studioId });
      mintedNewCustomer = true;
    }
  }

  // PINNED PATH: a held deposit + booking already exist. Attach the customer and
  // reuse the normal webhook path to confirm - do NOT mint a second deposit.
  // (`brain` is a point-in-time snapshot, so build the merged record directly
  // rather than re-reading it back out of `brain.deposits` after the upsert.)
  const pinned = link.pinnedDepositId ? brain.deposits[link.pinnedDepositId] : undefined;
  let deposit: Deposit;
  if (pinned) {
    // B5: the pin's deposit keeps naming its held booking (bk-pin-*, minted with
    // the hold), so the webhook confirms that booking rather than a new one.
    deposit = { ...pinned, customerId };
    brain.upsertDeposit(deposit);
    // The held booking is the hold itself (B5): it becomes the client's.
    const held = pinned.heldBookingId ? brain.bookings[pinned.heldBookingId] : undefined;
    if (held) brain.upsertBooking({ ...held, customerId });
  } else {
    // Open, client-pick path (no pin): mint a fresh deposit carrying a UNIQUE
    // per-booking reference (C5). mintBookingReference suffixes the link's shared
    // code (e.g. AL-LOCKE-4F7K-03), so two clients booking the same length never
    // collide on the reconciliation key (reference::amount) in depositReconFlow.
    const linkInput = {
      customerId,
      studioId: link.studioId,
      artistId: link.artistId,
      amountAud: SESSION_DEPOSIT_AUD[input.session],
      channel: 'sms' as const,
      sessionLength: input.session,
      source: 'artist-link' as const,
      depositReference: mintBookingReference(link),
    };
    if (!isStoreFallbackEnabled()) {
      // SERVER-BACKED atomic create (tv-rp7.2): this unpinned path creates a FRESH
      // booking AND its paid deposit in the SAME synchronous tick, whose two client
      // PUTs coalesce and deadlock on the circular booking.depositId <-> deposit.
      // bookingId FK (tv-3ps.6). Precompute the deposit id so this create's write is
      // SUPPRESSED (withServerAuthoredWrites) — otherwise the coalesced pending-deposit
      // PUT would race the intent — then route the confirm through the create-and-pay
      // intent below (atomicCreate). (Pinned & Stripe-link paths pre-commit the
      // deposit in an earlier flush, so they keep the two-write path.)
      const depId = `dep-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      let created!: Deposit;
      withServerAuthoredWrites([['deposits', depId]], () => {
        created = generateDepositLink({ id: depId, ...linkInput });
      });
      deposit = created;
    } else {
      deposit = generateDepositLink(linkInput);
    }
  }

  // Extend (not fork) the existing webhook path - it confirms the booking, marks
  // the deposit paid, and auto-sends the waiver on the normal cadence. Unpinned +
  // server-backed, that confirm is the ATOMIC create-and-pay (atomicCreate); the
  // pinned path (deposit + held booking already committed) keeps the two writes.
  const atomicCreate = !pinned && !isStoreFallbackEnabled();
  simulateStripeWebhookPaid({
    depositId: deposit.id,
    bookingStartIso: input.startIso,
    designBrief: input.designBrief,
    referencePhotos: input.referencePhotos,
    atomicCreate,
    // D05-11: the email the customer typed on the self-book page.
    ...(input.details.email ? { contactEmail: input.details.email.trim().toLowerCase() } : {}),
    // A freshly-minted customer must land on the server before the atomic intent
    // (FK). Persist it first and await 'ok'; a matched (already-persisted) customer
    // needs no prerequisite.
    ...(atomicCreate && mintedNewCustomer
      ? {
          beforeAtomicIntent: async () => {
            const record = useBrainStore.getState().customers[customerId];
            const outcome = await persistRecord('customers', customerId, record);
            if (outcome !== 'ok') throw new Error(`customer persist ${outcome}`);
          },
        }
      : {}),
  });

  // A pinned slot's booking is its hold (B5: minted by the server, not bk-<depositId>).
  const bookingId = deposit.heldBookingId ?? `bk-${deposit.id}`;
  // Universal hold window (15 Jul ruling): 24h inside 7 days, 48h outside.
  const heldUntilIso = holdExpiresAtIso(input.startIso, nowIso());
  const whenLabel = new Date(input.startIso).toLocaleString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const artist = brain.artists[link.artistId];
  const artistName = artist?.displayName ?? link.artistId;

  // Immediate confirmation text into the customer thread.
  brain.addConversationEntry(customerId, {
    id: `msg-artistlink-${deposit.id}`,
    customerId,
    channel: 'sms',
    direction: 'out',
    body: `You're booked in with ${artistName} for ${whenLabel}. Your deposit is paid. We'll send a reminder before the day.`,
    timestamp: nowIso(),
    authorRole: 'system',
  });

  brain.appendActivity({
    id: `act-artistlink-${deposit.id}`,
    type: 'booking-created',
    customerId,
    studioId: link.studioId,
    artistId: link.artistId,
    body: `New self-book via ${artistName}'s link (${deposit.depositReference}) for ${whenLabel}.`,
    timestamp: nowIso(),
  });

  // C6: alert the artist FIRST, in their own DM thread, then the shared channel.
  notifyArtistOfSelfBook({
    artistId: link.artistId,
    customerId,
    body: `📅 New self-book for ${whenLabel} · ${deposit.depositReference}. Deposit paid.`,
  });
  const target = autoPingChannelFor('deposit-paid', link.studioId, brain.internalComms);
  if (target) {
    brain.addAutoPing(
      target,
      `📅 ${artistName} just got a self-book for ${whenLabel} · ref ${deposit.depositReference}.`,
      { customerRefId: customerId, timestamp: nowIso() },
    );
  }

  // A universal link is reusable and stays active - clear any pin so the slot
  // stops being held (no pre-reserved slot once paid). A staff named link is
  // single-use - mark it paid.
  if (link.universal) {
    brain.upsertSelfBookLink({
      ...link,
      pinnedStartIso: undefined,
      sessionLength: undefined,
      pinnedDepositId: undefined,
      depositAud: undefined,
    });
  } else {
    brain.upsertSelfBookLink({ ...link, status: 'paid', bookingId });
  }

  return { customerId, depositId: deposit.id, bookingId, heldUntilIso };
}

/**
 * Sim-clock reconciler for expired artist self-book links (tv-311, Diagram 05/09,
 * DECIDED 18 Aug). Mirrors the sibling reconcilers (reconcileDepositHolds,
 * reconcileTopupSchedule): "now" is the sim clock, never Date.now(); it loops
 * every link, skips settled records, and persists via upsertSelfBookLink.
 *
 * When a NAMED (staff-sent) link passes its `expiresAt`, mark it `expired` - a
 * read-only "expired" notification on the artist's profile; the artist never
 * chases - and mint an AGENT hot lead (fresh cohort) so the follow-up re-enters
 * warm on the Agent worklist, NOT the Sweeper's expired gap bucket. Universal
 * (customer-agnostic, reusable) links carry no client to follow up, so they are
 * left untouched. Idempotent: the `expired` status guard stops a re-mint on the
 * next pass. Expired DEPOSIT links are unaffected (routeExpiredResend owns them).
 */
export function reconcileSelfBookLinkExpiry(): void {
  const brain = useBrainStore.getState();
  const now = new Date(nowIso()).getTime();

  for (const link of Object.values(brain.selfBookLinks)) {
    if (link.status === 'expired' || link.status === 'paid') continue;
    // Universal links are reusable and customer-agnostic - no client, no hot lead.
    if (link.universal || !link.customerId) continue;
    if (new Date(link.expiresAt).getTime() >= now) continue;

    const customerId = link.customerId;
    // Read-only artist-profile 'expired' notification (the artist never chases).
    brain.upsertSelfBookLink({ ...link, status: 'expired' });

    // The follow-up is the AGENT's: setSequenceState with a sourceConversationId
    // mints a fresh pushed-lead card via buildStandardQueue (agent lane), so the
    // client re-enters warm - NOT a Sweeper gap item.
    const existing = brain.sequenceState[customerId];
    brain.setSequenceState(customerId, {
      ...existing,
      stage: 1,
      cohort: 'fresh',
      redKind: undefined,
      nextDueAt: undefined,
      sourceConversationId: existing?.sourceConversationId ?? customerId,
      description: 'Self-book link expired - follow up as a hot lead',
    });

    brain.appendActivity({
      id: `act-selfbook-expired-${link.token}`,
      type: 'pushed-to-queue',
      customerId,
      studioId: link.studioId,
      artistId: link.artistId,
      body: 'Self-book link expired. Artist notified (read-only); following up as an Agent hot lead.',
      timestamp: nowIso(),
    });

    eventBus.publish('self-book-link:expired', {
      token: link.token,
      customerId,
      artistId: link.artistId,
    });
  }
}
